<template>
  <div class="tags-page">
    <div class="top-bar">
      <h1>Tag management</h1>
      <div class="actions">
        <input
          v-model.trim="newTag"
          class="tag-input"
          placeholder="New tag name…"
          @keydown.enter.prevent="createTag"
        />
        <button class="add-btn" @click="createTag" :disabled="saving || !newTag">
          Add tag
        </button>
      </div>
    </div>

    <div class="toolbar">
      <input
        v-model.trim="search"
        class="search-input"
        placeholder="Search tags…"
        @input="loadTags"
      />
      <button class="refresh-btn" @click="loadTags" :disabled="loading">
        {{ loading ? 'Refreshing…' : 'Refresh' }}
      </button>
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <div class="table-wrap">
      <table class="tags-table">
        <thead>
          <tr>
            <th>Tag</th>
            <th>Count</th>
            <th class="actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tag in tags" :key="tag.name">
            <td>
              <div v-if="editingTag === tag.name" class="edit-row">
                <input
                  v-model.trim="editValue"
                  class="tag-input"
                  placeholder="New tag name…"
                  @keydown.enter.prevent="saveEdit(tag.name)"
                />
              </div>
              <button
                v-else
                class="tag-link"
                type="button"
                @click="openTagEditor(tag)"
              >
                {{ tag.name }}
              </button>
            </td>
            <td>{{ tag.count }}</td>
            <td class="actions-col">
              <template v-if="editingTag === tag.name">
                <button class="save-btn" @click="saveEdit(tag.name)" :disabled="saving || !editValue">
                  Save
                </button>
                <button class="cancel-btn" @click="cancelEdit" :disabled="saving">
                  Cancel
                </button>
              </template>
              <template v-else>
                <button class="edit-btn" @click="startEdit(tag.name)">Rename</button>
                <button class="members-btn" @click="openTagEditor(tag)">Edit list</button>
                <button class="delete-btn" @click="deleteTag(tag.name)">Delete</button>
              </template>
            </td>
          </tr>
          <tr v-if="!tags.length && !loading">
            <td colspan="3" class="empty">No tags found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Editable membership checklist -->
    <div v-if="showTagModal" class="overlay">
      <div class="modal membership-modal">
        <div class="modal-header">
          <div>
            <h2>Edit tagged list</h2>
            <p class="modal-sub">{{ selectedTag }}</p>
          </div>
          <button class="close-btn" @click="closeTagModal">✕</button>
        </div>

        <p v-if="tagError" class="error">{{ tagError }}</p>
        <p v-else-if="tagLoading" class="loading-line">Loading contacts…</p>
        <template v-else>
          <p class="tag-summary">
            {{ selectedCount }} selected · {{ roster.length }} in list
          </p>

          <div class="search-row">
            <input
              v-model.trim="memberSearch"
              class="search-input"
              placeholder="Search all contacts to add…"
              @input="onMemberSearch"
            />
          </div>

          <div v-if="searchHits.length" class="search-hits">
            <button
              v-for="c in searchHits"
              :key="'hit-' + c.id"
              type="button"
              class="hit-row"
              @click="addToRoster(c)"
            >
              <span class="hit-name">{{ displayName(c) }}</span>
              <span class="hit-email">{{ c.main_email }}</span>
              <span class="hit-add">+ Add</span>
            </button>
          </div>

          <div class="checklist">
            <div
              v-for="c in roster"
              :key="c.id"
              class="check-row"
              :class="{ checked: selectedIds.has(c.id) }"
            >
              <input
                type="checkbox"
                :checked="selectedIds.has(c.id)"
                @change="toggleMember(c.id, $event.target.checked)"
              />
              <router-link
                class="check-name"
                :to="`/customers/${c.id}`"
                :title="'Open ' + displayName(c)"
              >{{ displayName(c) }}</router-link>
              <span class="check-email">{{ c.main_email }}</span>
              <router-link
                class="check-open"
                :to="`/customers/${c.id}`"
              >Open</router-link>
            </div>
            <p v-if="!roster.length" class="empty">No contacts yet. Search above to add people.</p>
          </div>

          <div class="mass-panel">
            <button type="button" class="mass-toggle" @click="showMassPanel = !showMassPanel">
              <span>{{ showMassPanel ? '▾' : '▸' }}</span>
              Also add / remove these people from other tags
            </button>
            <div v-if="showMassPanel" class="mass-body">
              <p class="mass-hint">
                Applies to the {{ selectedCount }} selected contact{{ selectedCount === 1 ? '' : 's' }} above
                (doesn’t change membership in “{{ selectedTag }}” until you save the list).
              </p>
              <div class="mass-chips">
                <button
                  v-for="t in otherTags"
                  :key="t.name"
                  type="button"
                  class="mass-chip"
                  :class="{ on: massTagSet.has(t.name) }"
                  @click="toggleMassTag(t.name)"
                >
                  {{ t.name }}
                </button>
                <p v-if="!otherTags.length" class="empty">No other tags yet.</p>
              </div>
              <div class="mass-actions">
                <button
                  type="button"
                  class="mass-add"
                  :disabled="!massTagSet.size || !selectedCount || !!massBusy"
                  @click="massApply('add')"
                >
                  {{ massBusy === 'add' ? 'Adding…' : 'Add to tags' }}
                </button>
                <button
                  type="button"
                  class="mass-remove"
                  :disabled="!massTagSet.size || !selectedCount || !!massBusy"
                  @click="massApply('remove')"
                >
                  {{ massBusy === 'remove' ? 'Removing…' : 'Remove from tags' }}
                </button>
              </div>
              <p v-if="massMsg" class="mass-msg" :class="{ err: massErr }">{{ massMsg }}</p>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="cancel-btn" @click="closeTagModal" :disabled="savingMembers">
              Cancel
            </button>
            <button
              type="button"
              class="save-btn"
              :disabled="savingMembers || !dirty"
              @click="saveMembership"
            >
              {{ savingMembers ? 'Saving…' : 'Save list' }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import TagsService from '@/services/TagsService'
import CustomersService from '@/services/CustomersService'

function debounce(fn, ms) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), ms)
  }
}

export default {
  name: 'TagManagement',
  data() {
    return {
      tags: [],
      search: '',
      newTag: '',
      loading: false,
      saving: false,
      error: '',
      editingTag: null,
      editValue: '',
      showTagModal: false,
      selectedTag: '',
      tagLoading: false,
      tagError: '',
      roster: [],
      selectedIds: new Set(),
      initialIds: new Set(),
      memberSearch: '',
      searchHits: [],
      savingMembers: false,
      onMemberSearch: null,
      showMassPanel: false,
      massTagSet: new Set(),
      massBusy: '',
      massMsg: '',
      massErr: false
    }
  },
  computed: {
    selectedCount() {
      return this.selectedIds.size
    },
    dirty() {
      if (this.selectedIds.size !== this.initialIds.size) return true
      for (const id of this.selectedIds) {
        if (!this.initialIds.has(id)) return true
      }
      return false
    },
    otherTags() {
      const cur = (this.selectedTag || '').toLowerCase()
      return (this.tags || []).filter((t) => t.name.toLowerCase() !== cur)
    }
  },
  created() {
    this.loadTags()
    this.onMemberSearch = debounce(this.fetchMemberHits, 280)
  },
  methods: {
    displayName(c) {
      return [c.first_name, c.last_name].filter(Boolean).join(' ') || c.main_email || `Contact #${c.id}`
    },
    async loadTags() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await TagsService.summary(this.search)
        this.tags = data
      } catch (e) {
        console.error('Failed to load tags', e)
        this.error = 'Could not load tags. Please try again.'
      } finally {
        this.loading = false
      }
    },
    async createTag() {
      if (!this.newTag) return
      this.saving = true
      this.error = ''
      try {
        await TagsService.create(this.newTag)
        this.newTag = ''
        await this.loadTags()
      } catch (e) {
        console.error('Failed to create tag', e)
        this.error = 'Could not create tag.'
      } finally {
        this.saving = false
      }
    },
    startEdit(name) {
      this.editingTag = name
      this.editValue = name
    },
    cancelEdit() {
      this.editingTag = null
      this.editValue = ''
    },
    async saveEdit(name) {
      if (!this.editValue) return
      this.saving = true
      this.error = ''
      try {
        await TagsService.rename(name, this.editValue)
        this.cancelEdit()
        await this.loadTags()
      } catch (e) {
        console.error('Failed to rename tag', e)
        this.error = 'Could not rename tag.'
      } finally {
        this.saving = false
      }
    },
    async deleteTag(name) {
      if (!confirm(`Delete "${name}" from all contacts?`)) return
      this.saving = true
      this.error = ''
      try {
        await TagsService.remove(name)
        await this.loadTags()
      } catch (e) {
        console.error('Failed to delete tag', e)
        this.error = 'Could not delete tag.'
      } finally {
        this.saving = false
      }
    },
    async openTagEditor(tag) {
      this.selectedTag = tag.name
      this.roster = []
      this.selectedIds = new Set()
      this.initialIds = new Set()
      this.memberSearch = ''
      this.searchHits = []
      this.tagError = ''
      this.tagLoading = true
      this.showTagModal = true
      this.showMassPanel = false
      this.massTagSet = new Set()
      this.massMsg = ''
      this.massErr = false
      this.massBusy = ''
      try {
        const { data } = await CustomersService.list({
          tag: tag.name,
          page: 1,
          per_page: 500
        })
        const customers = data.customers || []
        this.roster = customers
        const ids = new Set(customers.map((c) => c.id))
        this.selectedIds = new Set(ids)
        this.initialIds = new Set(ids)
      } catch (e) {
        console.error('Failed to load tag customers', e)
        this.tagError = 'Could not load contacts for this tag.'
      } finally {
        this.tagLoading = false
      }
    },
    closeTagModal() {
      this.showTagModal = false
      this.selectedTag = ''
      this.roster = []
      this.selectedIds = new Set()
      this.initialIds = new Set()
      this.memberSearch = ''
      this.searchHits = []
      this.tagError = ''
      this.tagLoading = false
      this.showMassPanel = false
      this.massTagSet = new Set()
      this.massMsg = ''
    },
    toggleMember(id, checked) {
      const next = new Set(this.selectedIds)
      if (checked) next.add(id)
      else next.delete(id)
      this.selectedIds = next
    },
    toggleMassTag(name) {
      const next = new Set(this.massTagSet)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      this.massTagSet = next
    },
    async massApply(mode) {
      const ids = Array.from(this.selectedIds)
      const tags = Array.from(this.massTagSet)
      if (!ids.length || !tags.length) return
      this.massBusy = mode
      this.massMsg = ''
      this.massErr = false
      try {
        const payload = { customer_ids: ids, tags }
        const { data } = mode === 'add'
          ? await CustomersService.bulkApplyTags(payload)
          : await CustomersService.bulkRemoveTags(payload)
        const n = data.updated || 0
        this.massMsg = mode === 'add'
          ? `Added tag${tags.length > 1 ? 's' : ''} on ${n} contact${n === 1 ? '' : 's'}.`
          : `Removed tag${tags.length > 1 ? 's' : ''} from ${n} contact${n === 1 ? '' : 's'}.`
        await this.loadTags()
      } catch (e) {
        console.error('Mass tag failed', e)
        this.massErr = true
        this.massMsg = 'Could not update other tags.'
      } finally {
        this.massBusy = ''
      }
    },
    async fetchMemberHits() {
      const q = this.memberSearch.trim()
      if (q.length < 2) {
        this.searchHits = []
        return
      }
      try {
        const { data } = await CustomersService.list({ q, page: 1, per_page: 20 })
        const rosterIds = new Set(this.roster.map((c) => c.id))
        this.searchHits = (data.customers || []).filter((c) => !rosterIds.has(c.id))
      } catch {
        this.searchHits = []
      }
    },
    addToRoster(c) {
      if (!this.roster.some((x) => x.id === c.id)) {
        this.roster = [c, ...this.roster]
      }
      const next = new Set(this.selectedIds)
      next.add(c.id)
      this.selectedIds = next
      this.memberSearch = ''
      this.searchHits = []
    },
    async saveMembership() {
      this.savingMembers = true
      this.tagError = ''
      try {
        await TagsService.setMembership(this.selectedTag, Array.from(this.selectedIds))
        this.closeTagModal()
        await this.loadTags()
      } catch (e) {
        console.error('Failed to save membership', e)
        this.tagError = 'Could not save tagged list.'
      } finally {
        this.savingMembers = false
      }
    }
  }
}
</script>

<style scoped>
.tags-page { padding: 0.5rem; }
.top-bar {
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:1rem;
}
.actions { display:flex; gap:.5rem; align-items:center; }
.tag-input {
  background: rgba(255,255,255,0.05);
  border:1px solid rgba(255,255,255,0.3);
  color:#fff;
  padding: .4rem .6rem;
  border-radius:6px;
}
.add-btn {
  background:#38a169;
  border:none;
  border-radius:6px;
  padding:.45rem .8rem;
  font-weight:700;
  color:#fff;
}
.toolbar {
  display:flex;
  gap:.75rem;
  align-items:center;
  margin-bottom:.75rem;
}
.search-input {
  flex:1;
  background: transparent;
  border:1px solid #fff;
  border-radius:5px;
  padding:.5rem .8rem;
  color:#fff;
}
.refresh-btn {
  background:#2d3748;
  border:none;
  border-radius:5px;
  padding:.45rem .8rem;
  color:#fff;
}
.table-wrap {
  border:1px solid rgba(255,255,255,.12);
  border-radius:6px;
  overflow:hidden;
}
.tags-table {
  width:100%;
  border-collapse: collapse;
}
.tags-table th,
.tags-table td {
  padding:.6rem .75rem;
  border-bottom:1px solid rgba(255,255,255,.08);
  text-align:left;
}
.tags-table th { background: rgba(255,255,255,.05); }
.actions-col { width: 280px; white-space: nowrap; }
.edit-row { display:flex; gap:.4rem; align-items:center; }
.edit-btn,
.delete-btn,
.save-btn,
.cancel-btn,
.members-btn {
  border:none;
  border-radius:5px;
  padding:.35rem .6rem;
  margin-right:.35rem;
  cursor:pointer;
  font-weight:600;
  color:#fff;
}
.edit-btn { background:#3182ce; }
.members-btn { background:#805ad5; }
.delete-btn { background:#e53e3e; }
.save-btn { background:#38a169; }
.cancel-btn { background:#4a5568; }
.empty { text-align:center; color:#cbd5e0; }
.error { color:#feb2b2; margin:.5rem 0; }
.tag-link {
  background:none;
  border:none;
  color:#fbd38d;
  cursor:pointer;
  padding:0;
  text-decoration:underline;
}
.tag-link:hover { color:#fff; }
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1rem;
}
.modal {
  width: 100%;
  max-width: 720px;
  background: linear-gradient(165deg, #1e1e2f, #151525);
  border: 1px solid rgba(167, 139, 250, 0.35);
  border-radius: 12px;
  padding: 1.25rem 1.4rem;
  color: #fff;
  box-shadow: 0 16px 48px rgba(0,0,0,.5);
  max-height: 88vh;
  overflow-y: auto;
}
.modal-header {
  display:flex;
  justify-content:space-between;
  align-items:flex-start;
  margin-bottom:.5rem;
}
.modal-header h2 { margin: 0; font-size: 1.2rem; }
.modal-sub {
  margin: 0.2rem 0 0;
  color: #fbd38d;
  font-size: 0.9rem;
}
.close-btn {
  background:none;
  border:none;
  color:#fff;
  cursor:pointer;
  font-size:1.1rem;
}
.tag-summary { color:#a7f3d0; margin:.35rem 0 .75rem; }
.loading-line { color: #cbd5e0; }
.search-row { margin-bottom: 0.5rem; }
.search-hits {
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  max-height: 160px;
  overflow-y: auto;
}
.hit-row {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0.45rem 0.65rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255,255,255,.06);
  color: #fff;
  text-align: left;
  cursor: pointer;
}
.hit-row:hover { background: rgba(128, 90, 213, 0.18); }
.hit-name { font-weight: 600; }
.hit-email { color: #cbd5e0; font-size: 0.85rem; }
.hit-add { color: #68d391; font-weight: 700; font-size: 0.8rem; }
.checklist {
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 8px;
  max-height: 36vh;
  overflow-y: auto;
}
.check-row {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 0.55rem;
  align-items: center;
  padding: 0.45rem 0.7rem;
  border-bottom: 1px solid rgba(255,255,255,.06);
}
.check-row.checked { background: rgba(56, 161, 105, 0.12); }
.check-name {
  font-weight: 600;
  color: #e9d5ff;
  text-decoration: none;
}
.check-name:hover { color: #fff; text-decoration: underline; }
.check-email { color: #cbd5e0; font-size: 0.85rem; }
.check-open {
  color: #90cdf4;
  font-size: 0.8rem;
  text-decoration: none;
}
.check-open:hover { text-decoration: underline; }
.mass-panel {
  margin-top: 0.85rem;
  border: 1px solid rgba(167, 139, 250, 0.25);
  border-radius: 10px;
  background: rgba(128, 90, 213, 0.08);
  overflow: hidden;
}
.mass-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  color: #e9d5ff;
  padding: 0.7rem 0.9rem;
  cursor: pointer;
  font-weight: 600;
  text-align: left;
}
.mass-body { padding: 0 0.9rem 0.9rem; }
.mass-hint {
  margin: 0 0 0.65rem;
  font-size: 0.82rem;
  color: rgba(255,255,255,.7);
  line-height: 1.4;
}
.mass-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
  max-height: 140px;
  overflow-y: auto;
}
.mass-chip {
  background: rgba(255,255,255,.06);
  border: 1px solid rgba(255,255,255,.2);
  color: #e2e8f0;
  border-radius: 999px;
  padding: 0.28rem 0.7rem;
  font-size: 0.8rem;
  cursor: pointer;
}
.mass-chip.on {
  background: rgba(251, 211, 141, 0.2);
  border-color: #fbd38d;
  color: #fbd38d;
}
.mass-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.mass-add, .mass-remove {
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
}
.mass-add { background: #38a169; }
.mass-remove { background: #c53030; }
.mass-add:disabled, .mass-remove:disabled { opacity: 0.45; cursor: not-allowed; }
.mass-msg { margin: 0.55rem 0 0; font-size: 0.85rem; color: #a7f3d0; }
.mass-msg.err { color: #feb2b2; }
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}
</style>
